import { useContext, useMemo } from 'react';
import { usePrintLengthUnit } from '../print/usePrintLengthUnit';
import { getFloorPlanePositionProperty, hasFloorPlanePosition } from '@pointorama/potree-shared';
import type { DocumentLayoutInput } from '@pointorama/print-engine';
import type { PrintFloorInfo } from 'shared-types';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { UserContext } from '../../contexts/UserContext';
import { useProject } from '../../hooks/potree/useProject';
import { useAnnotations } from '../../hooks/potree/useRenderer';
import { GroupType, type ProjectTemplateLibraryQuery } from '../../types/graphqlTypes';

function floorElevation(floorId: string): number {
  try {
    if (!hasFloorPlanePosition(floorId)) return 0;
    const z = getFloorPlanePositionProperty(floorId).value?.z;
    return typeof z === 'number' ? z : 0;
  } catch {
    return 0;
  }
}

/** The project's floors (FLOOR groups), lowest first. */
export function useProjectFloors(): PrintFloorInfo[] {
  const [{ indexedAnnotations }] = useAnnotations();
  return useMemo(
    () => indexedAnnotations.flatMap((a) => (isAnnotationContextGroup(a) && a.type === GroupType.Floor ? [{ id: a.identifier, name: a.name, elevation: floorElevation(a.identifier) }] : [])).sort((a, b) => a.elevation - b.elevation),
    [indexedAnnotations],
  );
}

/** Placeholder values every sheet of the project shares: project, organisation (brand kit), user. */
export function useSheetSources(library: ProjectTemplateLibraryQuery['projectTemplateLibrary'] | undefined, sheet: { name: string; number?: string | null } | null): DocumentLayoutInput['sources'] {
  const currentUser = useContext(UserContext);
  const projectQuery = useProject();
  const projectName = projectQuery.data?.projectById?.name ?? '';
  const units = usePrintLengthUnit();
  return useMemo(() => {
    const brand = library?.config.brand;
    const organisation = currentUser.organisations?.find((o) => o.id === library?.organisationId);
    return {
      project: { name: projectName },
      org: { name: organisation?.name ?? '', address: brand?.address ?? undefined, phone: brand?.phone ?? undefined, email: brand?.email ?? undefined, website: brand?.website ?? undefined, vat: brand?.vat ?? undefined },
      sheet: sheet ? { name: sheet.name, number: sheet.number ?? undefined } : undefined,
      user: { name: `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() },
      units,
    };
  }, [library, currentUser, projectName, sheet, units]);
}
